import React, { useState, useEffect } from "react";
import SearchForm from "../../stories/Forms/SearchForm";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import {
  links,
  searchFormFields,
  SelectedPatientFields,
} from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import formatData, { formatClinicalDtAge } from "../../utils/utils";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../components/AuthContext/AuthContext";
import useApis from "../../services/useAPI";
import {
  createClinicalPatientUrl,
  checkClinicalPatientUrl,
} from "../../utils/constants";
import ErrorToaster from "../../components/Shared/ErrorToaster";
import BackButton from "../../components/Shared/BackButton";
import PageLayout from "../../components/Layout/PageLayout";
import avatar from "../../assets/images/Avatar.png";
/**
 * SelectPatient component displays details of a patient record.
 * It includes a search form, a table for displaying modified bookings, and a back button.
 * @param {Object} props - Component props.
 * @param {Array} props.bookings - Modified bookings data.
 * @param {Object} props.user - User data.
 * @returns {JSX.Element|null} - Rendered component or null if user data is not available.
 */
function SelectPatient() {
  const auth = useAuth();
  auth.setTitle("PARTICIPANT RECORD");
  const location = useLocation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const { callApi, resultPost, serverError, tokenError, toggleShowError } =
    useApis();
  const [fields, setFields] = useState(SelectedPatientFields);
  let selectedService = localStorage.getItem("selectedService");
  const clinicalDetails =
    JSON.parse(sessionStorage.getItem("clinicalPatientDetails")) ?? {};
  const stages = [
    "patient-record",
    "select-patient",
    "patient-details",
    "confirm-details",
    "enter-barcode",
    "appointment-confirmation",
  ];

  useEffect(() => {
    if (
      !stages.includes(sessionStorage.getItem("allowedStage")) &&
      auth?.featureFlag
    ) {
      navigate("/patient-record");
    }
  }, [sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    if (selectedService === "healthCare") {
      setFields(searchFormFields);
    } else {
      auth.setTitle("Clinical studies");
      setFields(SelectedPatientFields);
    }
  }, [selectedService, searchFormFields, SelectedPatientFields]);

  /* Modify user data to format date of birth */
  const userData =
    resultPost?.user_info?.length > 0 ? resultPost.user_info[0] : null;

  const userInfo = userData
    ? {
        ...userData,
        date_of_birth: formatClinicalDtAge(userData.date_of_birth),
      }
    : null;

  const flattenObject = (obj, parentKey = "", res = {}) => {
    for (let key in obj) {
      if (
        obj[key] &&
        typeof obj[key] === "object" &&
        !Array.isArray(obj[key])
      ) {
        flattenObject(obj[key], `${parentKey}${key}.`, res);
      } else {
        res[`${parentKey}${key}`] = obj[key];
      }
    }
    return res;
  };

  /**
   * Handles form submission for searching patient records.
   */
  const handleSubmit = async (data) => {
    const formattedData = {
      ...formatData(data),
    };
    await callApi(formattedData, checkClinicalPatientUrl, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
  };

  const handleFormSubmit = async () => {
    await callApi(
     clinicalDetails,
      createClinicalPatientUrl,
      "POST",
      {
        Authorization: `Bearer ${
          localStorage.getItem("access_token") || auth.accessToken
        }`,
        "Content-Type": "application/json",
      },
    );
    auth?.allowStage("select-patient");
    navigate("/patient-record/clinical-details", {
      state: {
        data: clinicalDetails,
        user: resultPost?.user_info,
        isDuplicate: "true",
        id: resultPost.booking?.[0]?.phlebotomist_booking_id,
        isChildDisplay: true,
      },
    });
  };

  useEffect(() => {
    if (!location.state?.data) {
      return;
    }

    handleSubmit(
      location.state.data
        ? location.state.data
        : location.state.resultPost.bookings[0],
    );
  }, [location.state]);

  useEffect(() => {
    if (selectedService === "clinical") {
      localStorage.setItem("patientCard", "false");
    }
  }, [selectedService]);

  useEffect(() => {
    if (location.state?.resultPost?.bookings) {
      setBookings(location.state.resultPost.bookings);
    }
  }, [location.state]);

  const handleBackClick = () => {
    navigate(-1);
  };
  const capitalize = (str = "") =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const fullName =
    userInfo?.first_name && userInfo?.last_name
      ? `${capitalize(userInfo.first_name)} ${capitalize(userInfo.last_name)}`
      : "";
  const isVhLogin = auth?.featureFlag;

  const formatDateWithAge = (dateString) => {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
        return "";
    }

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    const today = new Date();
    let age = today.getFullYear() - year;

    const hasBirthdayPassed =
        today.getMonth() + 1 > Number(month) ||
        (today.getMonth() + 1 === Number(month) &&
         today.getDate() >= Number(day));

    if (!hasBirthdayPassed) {
        age--;
    }

    return `${day}/${month}/${year} (${age} years)`;
}

const formatName = (value) => {
  const nameMap = {
    "thrive-registry": "Thrive Registry",
    "thrive-panel": "Thrive Panel",
  };

  return nameMap[value] || value;
}
  return (
    
      isVhLogin ? (
        <LoggedInLayout isVhlogin={isVhLogin} vitalityLayout={isVhLogin}>
          <PageLayout
            heading="Participant record found"
            className="vh-clinical-record-found d-flex flex-column"
            bodyClassName="d-flex flex-column"
          >
            <div className="card">
              <div className="card-header">
                <img src={avatar} alt="Avatar logo" />
                <span>{fullName}</span>
              </div>
              <div className="card-body">
                <div className="container">
                  <div className="row">
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">Study</span>
                      <span className="value">{formatName(clinicalDetails.study)}</span>
                    </div>
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">Test Type</span>
                      <span className="value">{formatName(clinicalDetails.test_type)}</span>
                    </div>
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">First Name</span>
                      <span className="value">{clinicalDetails.first_name}</span>
                    </div>
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">Last Name</span>
                      <span className="value">{clinicalDetails.last_name}</span>
                    </div>
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">Date of Birth</span>
                      <span className="value">{formatDateWithAge(clinicalDetails.date_of_birth)}</span>
                    </div>
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">Sex at Birth</span>
                      <span className="value">{clinicalDetails.sex_at_birth}</span>
                    </div>
                    <div className="col-sm-4 d-flex flex-column">
                      <span className="boots-label-styling">
                        Crio Identifier (if available)
                      </span>
                      <span className="value">
                        {clinicalDetails.crio_identifier ||
                          "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button className="boots-primary-btn-styling mt-3 align-self-end" onClick={handleFormSubmit}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
              >
                <path
                  d="M18.4141 8.70703L9.70703 17.4141L8.29297 16L14.5859 9.70703H0V7.70703H14.5859L8.29297 1.41406L9.70703 0L18.4141 8.70703Z"
                  fill="white"
                />
              </svg>
              Continue
            </button>
            {/* <SearchForm
      colStructure={6}
      fieldError={true}
      fields={fields}
      formError={getFormErrorMessage(selectedService)}
      onSubmit={handleSubmit}
      validationSchema={dateValidationSchema}
      serverError={serverError}
      tokenError={tokenError}
      subtitle="null"
      title="null"
    /> */}
          </PageLayout>
        </LoggedInLayout>
      ) : (
        <LoggedInLayout
          buttonLabel={"Logout"}
          img={img}
          isActivation={false}
          links={links}
          logo={logo}
          pageTitle={auth.title}
          userInitials={auth.userInitials}
        >
          <div className="patient-details-page patient-select-page">
            <div className="container">
              {serverError && <span className="server-error">{serverError}</span>}
              <div className="row">
                <div className="col-md-12 thrive-wrapper">
                  <SearchForm
                    colStructure={6}
                    formClass={"thrive-clinical-content"}
                    data={userInfo}
                    fields={fields}
                    onSubmit={handleFormSubmit}
                    bookings={bookings}
                    className=""
                    subtitle=""
                    title="Participant record found"
                    nametag={
                      <>
                        <h3 className="card-header">{fullName}</h3>
                        <div className="patient-card-seperator"></div>
                      </>
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <BackButton onClick={handleBackClick} />
                </div>
              </div>
            </div>
          </div>
          <ErrorToaster error={tokenError} onClose={toggleShowError} />
        </LoggedInLayout>
      )
    
  );
}
export default SelectPatient;
